import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { getAllFolders } from '../../utils/api';
import { FolderDoc, FileDoc } from '../../types/forms-docs';
import { Folder, File, ChevronRight, ChevronDown, Loader2 } from 'lucide-react';
import { LoadingSkeleton } from '../common/LoadingSkeleton';
import { ErrorState } from '../common/ErrorState';
import { EmptyState } from '../common/EmptyState';
import { TreeView } from './TreeView';

export function FormsDocsView() {
  const navigate = useNavigate();
  const { accessToken, isAuthenticated } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [folders, setFolders] = useState<FolderDoc[]>([]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const fetchFolders = async () => {
    if (!accessToken) {
      setError('Please log in to view documents');
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      const data = await getAllFolders(accessToken);
      // Sort folders by ID
      const sortedFolders = data.sort((a, b) => a.id - b.id);
      setFolders(sortedFolders);
      setError(null);
    } catch (error: any) {
      setError(error.message || 'Failed to fetch folders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      fetchFolders();
    }
  }, [accessToken]);

  const handleFileClick = (file: FileDoc) => {
    window.open(file.url, '_blank');
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <LoadingSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <ErrorState message={error} onRetry={fetchFolders} />
      </div>
    );
  }

  // Get root level folders, sorted by ID
  const rootFolders = folders
    .filter(folder => !folder.parentId)
    .sort((a, b) => a.id - b.id);

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="bg-light-card dark:bg-dark-card rounded-lg border border-light-border dark:border-dark-border p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">
          Forms & Documents
        </h2>

        {rootFolders.length === 0 ? (
          <EmptyState message="No documents available" />
        ) : (
          <div className="space-y-2">
            {rootFolders.map(folder => (
              <TreeView
                key={folder.id}
                folder={folder}
                allFolders={folders}
                onFileClick={handleFileClick}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}